import { Spinner } from "./Spinner.js"

/** Server lifecycle states, FRONTEND_REFERENCE.md section 14, plus "unknown"
 *  (ServerStatusReply.state, @mc/types: server-manager answered but Crafty
 *  itself is unreachable, distinct from "offline" which means Crafty
 *  confirmed the server just isn't running). Every state has both a color
 *  AND a text label — never color alone (accessibility, and the project's
 *  own "colors reinforce, text confirms" principle). */
export type ServerState = "online" | "offline" | "starting" | "stopping" | "restarting" | "crashed" | "unknown"

const STATE_META: Record<ServerState, { label: string; color: string; pulse: boolean }> = {
  online: { label: "En línea", color: "var(--status-success)", pulse: true },
  offline: { label: "Apagado", color: "var(--text-muted)", pulse: false },
  starting: { label: "Iniciando…", color: "var(--status-info)", pulse: false },
  stopping: { label: "Deteniendo…", color: "var(--status-warning)", pulse: false },
  restarting: { label: "Reiniciando…", color: "var(--status-info)", pulse: false },
  crashed: { label: "Se detuvo inesperadamente", color: "var(--status-error)", pulse: false },
  unknown: { label: "Panel de control sin responder", color: "var(--status-warning)", pulse: false },
}

const TRANSITIONAL: ServerState[] = ["starting", "stopping", "restarting"]

export function StatusDot({ state }: { state: ServerState }) {
  const meta = STATE_META[state]
  const showSpinner = TRANSITIONAL.includes(state)

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-2)" }}>
      {showSpinner ? (
        <Spinner size="sm" />
      ) : (
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "var(--radius-full)",
            background: meta.color,
            animation: meta.pulse ? "mc-pulse 1.6s ease-in-out infinite" : undefined,
          }}
        />
      )}
      <span style={{ fontSize: "var(--text-sm)", color: "var(--text-primary)" }}>{meta.label}</span>
      <style>{`
        @keyframes mc-pulse {
          0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--status-success) 45%, transparent); }
          50% { box-shadow: 0 0 0 5px transparent; }
        }
      `}</style>
    </span>
  )
}
